/**
 * Integrations catalog (#67 productize) — the "integrate many backends, pick which
 * per client" layer. Same pattern as the module (#67a) + feature (#67e) catalogs,
 * one level out: a registry of backend integrations grouped by capability DOMAIN
 * (HCM, payroll, ERP/finance, MES, supply chain, notifications, SSO, data source),
 * each a pluggable adapter you select per fork.
 *
 * Selection is BUILD-TIME via `VITE_INTEGRATIONS` (allow/deny list). Layering:
 *   modules (#67a) → features (#67e) → INTEGRATIONS (this) → the vendor adapter.
 *
 * Two-tier status:
 *   - 'available' = an adapter exists in code today (Supabase data source, the
 *     org-import JSON connector, SAP charge-objects/CATS, Entra/MS-Graph SSO).
 *   - 'planned'   = a connection point only — catalogued + selectable so the
 *     fork-authoring UI lists it, but its adapter is built on customer demand.
 * The JSON/manual connector is the universal fallback for any 'planned' source.
 *
 * P1 = catalog + `isIntegrationEnabled` resolver + the Client Setup tick-list
 * (the "many options, choose per client" surface). Per-domain adapter CONTRACTS +
 * wiring each real vendor adapter into its seam is the ongoing follow-up (P2), one
 * per vendor. Spec context: Tasks/SAP_vs_Supabase_Hosting_Decision_2026-07-08.md +
 * FORK_PLAYBOOK.md (the fork's integration config).
 */

export type IntegrationDomain =
  | 'data-source'
  | 'hcm'
  | 'payroll'
  | 'erp-finance'
  | 'plm'
  | 'mes'
  | 'scm'
  | 'notifications'
  | 'sso'

export interface IntegrationDomainDef {
  key: IntegrationDomain
  label: string
  blurb: string
}

/** Domains in display order for the Client Setup grouping. */
export const INTEGRATION_DOMAINS: IntegrationDomainDef[] = [
  { key: 'data-source', label: 'Data backend', blurb: 'The system of record the app reads/writes.' },
  { key: 'hcm', label: 'HCM / org & people', blurb: 'Org units, people, and role assignments.' },
  { key: 'payroll', label: 'Payroll', blurb: 'Worker records, pay, and employee-number crosswalk.' },
  { key: 'erp-finance', label: 'ERP / Finance', blurb: 'Charge objects, WBS, cost postings, CATS write-back.' },
  { key: 'plm', label: 'Product lifecycle (PLM)', blurb: 'Released parts, manufacturing BOMs, and bills of process that SAP must mirror.' },
  { key: 'mes', label: 'Manufacturing (MES)', blurb: 'Shop-floor order status, execution, and component issues.' },
  { key: 'scm', label: 'Supply chain', blurb: 'Demand / supply planning systems.' },
  { key: 'notifications', label: 'Notifications', blurb: 'SMS / email dispatch.' },
  { key: 'sso', label: 'Identity / SSO', blurb: 'Login identity provider.' },
]

export type IntegrationStatus = 'available' | 'planned'
export type IntegrationDirection = 'source' | 'sink' | 'bidirectional'

export interface IntegrationDef {
  /** Stable key `domain.vendor` (e.g. 'hcm.successfactors'). Written in VITE_INTEGRATIONS. */
  key: string
  domain: IntegrationDomain
  vendor: string
  label: string
  description: string
  /** 'available' = adapter exists; 'planned' = connection point, adapter built on demand. */
  status: IntegrationStatus
  direction: IntegrationDirection
  /** Default for a fresh build. Only always-present, no-credential integrations default on. */
  default: 'on' | 'off'
}

export const INTEGRATIONS_CATALOG: IntegrationDef[] = [
  // ── Data backend ─────────────────────────────────────────────────────
  { key: 'data-source.supabase', domain: 'data-source', vendor: 'Supabase', label: 'Supabase (cloud Postgres)', description: 'Managed Postgres + Realtime — the default backend (clone-per-customer).', status: 'available', direction: 'bidirectional', default: 'on' },
  { key: 'data-source.sap-s4hana', domain: 'data-source', vendor: 'SAP', label: 'SAP S/4HANA (as backend)', description: 'SAP as the system of record via the `api` data-source seam. See the hosting decision doc.', status: 'planned', direction: 'bidirectional', default: 'off' },

  // ── HCM / org & people ───────────────────────────────────────────────
  { key: 'hcm.json-file', domain: 'hcm', vendor: 'File / JSON', label: 'JSON / File import', description: 'Paste or drop an org+people export. The demo-safe universal connector (org-import).', status: 'available', direction: 'source', default: 'on' },
  { key: 'hcm.successfactors', domain: 'hcm', vendor: 'SAP SuccessFactors', label: 'SuccessFactors', description: 'Org units, people, and roles from the company-wide system of record.', status: 'planned', direction: 'source', default: 'off' },
  { key: 'hcm.workday', domain: 'hcm', vendor: 'Workday', label: 'Workday HCM', description: 'Org + worker master data from Workday.', status: 'planned', direction: 'source', default: 'off' },

  // ── Payroll ──────────────────────────────────────────────────────────
  // 'available' as of Time Evaluation Phase 4b: `integrations/payroll/adpPayrollAdapter.ts`
  // pulls the File #/Co Code crosswalk and produces a WFN paydata CSV. The export leg is a
  // FILE, not an API call — ADP WFN has no general-purpose public paydata write endpoint on
  // most contracts. Stays default 'off': it needs a per-client Co Code map + crosswalk first.
  { key: 'payroll.adp', domain: 'payroll', vendor: 'ADP', label: 'ADP', description: 'Payroll worker records + ADP↔SAP employee-number crosswalk, and a Workforce Now paydata CSV export.', status: 'available', direction: 'bidirectional', default: 'off' },
  { key: 'payroll.paychex', domain: 'payroll', vendor: 'Paychex', label: 'Paychex', description: 'Payroll worker records and pay data.', status: 'planned', direction: 'bidirectional', default: 'off' },

  // ── ERP / Finance ────────────────────────────────────────────────────
  { key: 'erp-finance.sap-cats', domain: 'erp-finance', vendor: 'SAP', label: 'SAP charge objects + CATS', description: 'Pull charge objects / WBS / holidays; post approved labor to CATS/CATZ.', status: 'available', direction: 'bidirectional', default: 'off' },
  { key: 'erp-finance.oracle', domain: 'erp-finance', vendor: 'Oracle', label: 'Oracle ERP / NetSuite', description: 'Charge structures and cost postings from Oracle-family ERP.', status: 'planned', direction: 'bidirectional', default: 'off' },

  // ── Manufacturing (MES) ──────────────────────────────────────────────
  { key: 'mes.plex', domain: 'mes', vendor: 'Plex', label: 'Plex MES', description: 'Shop-floor order status and execution.', status: 'planned', direction: 'source', default: 'off' },
  { key: 'mes.sap-dmc', domain: 'mes', vendor: 'SAP DMC', label: 'SAP Digital Manufacturing', description: 'MES bridge, live order status, earned-vs-actual.', status: 'planned', direction: 'source', default: 'off' },
  { key: 'mes.opcenter', domain: 'mes', vendor: 'Siemens', label: 'Siemens Opcenter Execution', description: 'Component issues per order for the SAP goods-issue reconciliation.', status: 'planned', direction: 'source', default: 'off' },
  { key: 'mes.solumina', domain: 'mes', vendor: 'iBase-t', label: 'Solumina', description: 'A&D MES: component issues and as-built records per work order.', status: 'planned', direction: 'source', default: 'off' },
  { key: 'mes.apriso', domain: 'mes', vendor: 'Dassault', label: 'DELMIA Apriso', description: 'Component consumption per order.', status: 'planned', direction: 'source', default: 'off' },
  { key: 'mes.file', domain: 'mes', vendor: 'File', label: 'MES extract (file)', description: 'Canonical component-issue extract (.xlsx / .csv) uploaded per reconciliation run.', status: 'available', direction: 'source', default: 'on' },

  // ── Product lifecycle (PLM) ────────────────────────────────────────────────
  { key: 'plm.teamcenter', domain: 'plm', vendor: 'Siemens', label: 'Teamcenter', description: 'Released parts, MBOMs, and bills of process for the SAP reconciliation.', status: 'planned', direction: 'source', default: 'off' },
  { key: 'plm.windchill', domain: 'plm', vendor: 'PTC', label: 'Windchill', description: 'Released parts, MBOMs, and process plans for the SAP reconciliation.', status: 'planned', direction: 'source', default: 'off' },
  { key: 'plm.3dexperience', domain: 'plm', vendor: 'Dassault', label: '3DEXPERIENCE / ENOVIA', description: 'Released parts and manufacturing BOMs.', status: 'planned', direction: 'source', default: 'off' },
  { key: 'plm.aras', domain: 'plm', vendor: 'Aras', label: 'Aras Innovator', description: 'Released parts, MBOMs, and MPPs.', status: 'planned', direction: 'source', default: 'off' },
  { key: 'plm.arena', domain: 'plm', vendor: 'PTC', label: 'Arena PLM', description: 'Released items and BOMs.', status: 'planned', direction: 'source', default: 'off' },
  { key: 'plm.file', domain: 'plm', vendor: 'File', label: 'PLM extract (file)', description: 'Canonical parts / BOM / operations extract (.xlsx / .csv) uploaded per reconciliation run.', status: 'available', direction: 'source', default: 'on' },

  // ── Supply chain ─────────────────────────────────────────────────────
  { key: 'scm.kinaxis', domain: 'scm', vendor: 'Kinaxis', label: 'Kinaxis RapidResponse', description: 'Demand / supply planning signals.', status: 'planned', direction: 'source', default: 'off' },
  { key: 'scm.o9', domain: 'scm', vendor: 'o9', label: 'o9 Solutions', description: 'Integrated demand + supply planning.', status: 'planned', direction: 'source', default: 'off' },

  // ── Notifications ────────────────────────────────────────────────────
  { key: 'notifications.twilio', domain: 'notifications', vendor: 'Twilio', label: 'Twilio (SMS)', description: 'SMS dispatch for alerts and reminders.', status: 'planned', direction: 'sink', default: 'off' },
  { key: 'notifications.sendgrid', domain: 'notifications', vendor: 'SendGrid', label: 'SendGrid (email)', description: 'Transactional email dispatch.', status: 'planned', direction: 'sink', default: 'off' },

  // ── Identity / SSO ───────────────────────────────────────────────────
  { key: 'sso.entra', domain: 'sso', vendor: 'Microsoft Entra', label: 'Microsoft Entra ID', description: 'Azure AD / Entra SSO (MS Graph — env wiring exists).', status: 'available', direction: 'source', default: 'off' },
  { key: 'sso.okta', domain: 'sso', vendor: 'Okta', label: 'Okta', description: 'Okta SSO identity provider.', status: 'planned', direction: 'source', default: 'off' },
]

const BY_KEY = new Map(INTEGRATIONS_CATALOG.map((i) => [i.key, i]))

/** Parse `VITE_INTEGRATIONS` as an allow/deny list: `+key`/bare enables, `-key` disables. */
function parseOverrides(): Map<string, boolean> {
  const raw = (import.meta.env as Record<string, string | undefined>).VITE_INTEGRATIONS?.trim()
  const map = new Map<string, boolean>()
  if (!raw) return map
  for (const tokenRaw of raw.split(',')) {
    const token = tokenRaw.trim()
    if (!token) continue
    if (token.startsWith('-')) map.set(token.slice(1), false)
    else if (token.startsWith('+')) map.set(token.slice(1), true)
    else map.set(token, true)
  }
  return map
}

const OVERRIDES = parseOverrides()

/**
 * Is an integration enabled for this deployment? `VITE_INTEGRATIONS` override, else
 * the catalog default. Unknown keys are **fail-CLOSED** (return false) — you never
 * silently "enable" a backend that isn't catalogued (the inverse of `isFeatureBuilt`,
 * because enabling an unknown integration is riskier than hiding an unknown feature).
 */
export function isIntegrationEnabled(key: string): boolean {
  const def = BY_KEY.get(key)
  if (!def) return false
  const override = OVERRIDES.get(key)
  if (override !== undefined) return override
  return def.default === 'on'
}

/** Integrations in a domain, in catalog order (for the Client Setup grouping). */
export function integrationsForDomain(domain: IntegrationDomain): IntegrationDef[] {
  return INTEGRATIONS_CATALOG.filter((i) => i.domain === domain)
}
